import React, { useState, useEffect } from "react";
import { Tabs, Button, Spin, Empty, Select, message } from "antd";
import { 
  TrophyOutlined, 
  SwapOutlined, 
  UsergroupAddOutlined, 
  CrownOutlined,
  RiseOutlined,
  FallOutlined,
  MinusOutlined
} from "@ant-design/icons";
import { useAccount } from "wagmi";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import "../styles/global-background.css";
import "../styles/ClubPage.css";
import { useClub } from "../hooks/useClub";
import { Club, ClubRankingEntry } from "../types/club";

const { Option } = Select;

const ClubPage: React.FC = () => {
  const [language, setLanguage] = useState<"en" | "zh">("zh");
  const [selectedTransferClub, setSelectedTransferClub] = useState<number | null>(null);
  
  const { address } = useAccount();
  const { state, nctBalance, isApproving, actions } = useClub();

  // 渲染俱乐部卡片
  const renderClubCard = (club: Club) => {
    const themeClass = club.id === 1 ? 'club-theme-pink' : 
                      club.id === 2 ? 'club-theme-teal' : 'club-theme-orange';
    
    const getActionButton = () => {
      if (!address) {
        return (
          <button className="club-action join" disabled>
            请先连接钱包
          </button>
        );
      }

      if (!state.playerStatus?.isJoined) {
        return (
          <button 
            className="club-action join"
            onClick={() => actions.joinClub(club.id)}
            disabled={state.loading}
          >
            {state.loading ? "加入中..." : `加入 ${club.name}`}
          </button>
        );
      }

      if (state.playerStatus.clubId === club.id) {
        return (
          <button className="club-action joined" disabled>
            ✓ 已加入
          </button>
        );
      }

      return (
        <button 
          className="club-action transfer"
          onClick={() => {
            setSelectedTransferClub(club.id);
            if (state.playerStatus?.clubId) {
              actions.calculateTransferCost(state.playerStatus.clubId, club.id);
            }
          }}
          disabled={!state.playerStatus?.canTransfer}
        >
          {state.playerStatus?.canTransfer ? `转会至此` : "转会冷却中"}
        </button>
      );
    };

    return (
      <div key={club.id} className={`club-card ${themeClass}`}>
        <div className="club-ranking">#{club.ranking}</div>
        
        <div className="club-header">
          <div className="club-logo">{club.logo}</div>
          <div className="club-info">
            <h3>{club.name}</h3>
            <p className="club-kol">KOL: {club.kol.name}</p>
          </div>
        </div>

        <div className="club-stats">
          <div className="club-stat">
            <span className="club-stat-value">{club.memberCount}</span>
            <span className="club-stat-label">成员数</span>
          </div>
          <div className="club-stat">
            <span className="club-stat-value">{club.averageScore.toLocaleString()}</span>
            <span className="club-stat-label">平均积分</span>
          </div>
        </div>

        <p className="club-description">{club.description}</p>
        
        {getActionButton()}
      </div>
    );
  };

  // 渲染俱乐部总览
  const renderClubsOverview = () => (
    <div className="clubs-overview">
      {state.clubs.map(renderClubCard)}
    </div>
  );

  // 渲染我的俱乐部
  const renderMyClub = () => {
    if (!address) {
      return (
        <div className="club-error">
          <UsergroupAddOutlined style={{ fontSize: '3rem', marginBottom: '1rem' }} />
          <h3>请先连接钱包查看俱乐部信息</h3>
        </div>
      );
    }

    if (!state.playerStatus?.isJoined) {
      return (
        <div className="club-error">
          <UsergroupAddOutlined style={{ fontSize: '3rem', marginBottom: '1rem' }} />
          <h3>您还未加入任何俱乐部</h3>
          <p>请前往"俱乐部总览"选择并加入一个俱乐部</p>
        </div>
      );
    }

    const club = state.playerStatus.club!;
    const themeClass = club.id === 1 ? 'club-theme-pink' : 
                      club.id === 2 ? 'club-theme-teal' : 'club-theme-orange';

    return (
      <div className={`my-club-container ${themeClass}`}>
        <div className="my-club-card">
          <div className="my-club-header">
            <div className="my-club-logo">{club.logo}</div>
            <div className="my-club-info">
              <h2>{club.name}</h2>
              <p className="my-club-kol">队长: {club.kol.name}</p>
              <p style={{ color: 'rgba(255,255,255,0.7)', margin: 0 }}>
                加入时间: {new Date(state.playerStatus.joinDate!).toLocaleDateString()}
              </p>
            </div>
          </div>

          <div className="contribution-stats">
            <div className="contribution-card">
              <div className="contribution-value">{state.playerStatus.personalScore}</div>
              <div className="contribution-label">个人积分</div>
            </div>
            <div className="contribution-card">
              <div className="contribution-value">{state.playerStatus.contribution}</div>
              <div className="contribution-label">俱乐部贡献</div>
            </div>
            <div className="contribution-card">
              <div className="contribution-value">#{club.ranking}</div>
              <div className="contribution-label">俱乐部排名</div>
            </div>
          </div>

          {state.playerStatus.canTransfer && (
            <Button 
              type="primary" 
              icon={<SwapOutlined />}
              style={{ marginTop: '1rem' }}
              onClick={() => {
                message.info('请前往"转会中心"进行转会操作');
              }}
            >
              转会到其他俱乐部
            </Button>
          )}
        </div>
      </div>
    );
  };

  // 渲染转会中心
  const renderTransferCenter = () => {
    if (!address) {
      return (
        <div className="club-error">
          <SwapOutlined style={{ fontSize: '3rem', marginBottom: '1rem' }} />
          <h3>请先连接钱包</h3>
        </div>
      );
    }

    if (!state.playerStatus?.isJoined) {
      return (
        <div className="club-error">
          <SwapOutlined style={{ fontSize: '3rem', marginBottom: '1rem' }} />
          <h3>您需要先加入俱乐部才能转会</h3>
        </div>
      );
    }

    if (!state.playerStatus.canTransfer) {
      return (
        <div className="club-error">
          <SwapOutlined style={{ fontSize: '3rem', marginBottom: '1rem' }} />
          <h3>转会冷却中</h3>
          <p>请等待冷却时间结束后再进行转会</p>
        </div>
      );
    }

    const currentClub = state.playerStatus.club!;
    const availableClubs = state.clubs.filter(club => club.id !== currentClub.id);

    return (
      <div className="transfer-center">
        <div className="transfer-flow">
          <div className="transfer-section">
            <div className="transfer-section-header">
              <span className="transfer-label">当前俱乐部</span>
              <span className="nclab-balance">NClab余额: {nctBalance}</span>
            </div>
            <div className="transfer-club-info">
              <div className="transfer-club-logo">{currentClub.logo}</div>
              <div className="transfer-club-details">
                <h4>{currentClub.name}</h4>
                <p>KOL: {currentClub.kol.name}</p>
              </div>
            </div>
          </div>

          <div className="transfer-switch">
            <button className="transfer-switch-btn">
              <SwapOutlined />
            </button>
          </div>

          <div className="transfer-section">
            <div className="transfer-section-header">
              <span className="transfer-label">转会目标</span>
            </div>
            
            <Select
              style={{ width: '100%', marginBottom: '1rem' }}
              placeholder="选择要转会的俱乐部"
              value={selectedTransferClub}
              onChange={(value) => {
                setSelectedTransferClub(value);
                actions.calculateTransferCost(currentClub.id, value);
              }}
            >
              {availableClubs.map(club => (
                <Option key={club.id} value={club.id}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span>{club.logo}</span>
                    <span>{club.name}</span>
                  </div>
                </Option>
              ))}
            </Select>

            {selectedTransferClub && (
              <div className="transfer-club-info">
                {(() => {
                  const targetClub = availableClubs.find(c => c.id === selectedTransferClub)!;
                  return (
                    <>
                      <div className="transfer-club-logo">{targetClub.logo}</div>
                      <div className="transfer-club-details">
                        <h4>{targetClub.name}</h4>
                        <p>KOL: {targetClub.kol.name}</p>
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
          </div>

          {selectedTransferClub && (
            <>
              <div className="transfer-cost-info">
                <div className="transfer-cost">
                  转会费用: {state.transferCost.toLocaleString()} NClab
                </div>
                <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)', marginTop: '0.5rem' }}>
                  费用根据目标俱乐部人气和排名动态计算
                </div>
              </div>

              {/* 授权状态显示 */}
              {isApproving && (
                <div className="transfer-status-info" style={{
                  background: 'rgba(255, 193, 7, 0.1)',
                  border: '1px solid rgba(255, 193, 7, 0.3)',
                  borderRadius: '12px',
                  padding: '1rem',
                  margin: '1rem 0',
                  textAlign: 'center'
                }}>
                  <div style={{ color: '#ffc107', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                    🔐 授权进行中...
                  </div>
                  <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)' }}>
                    正在授权，请在钱包中确认交易
                  </div>
                </div>
              )}

              {/* 转会交易状态显示 */}
              {(state.isTransferring || state.isProcessingTransfer) && (
                <div className="transfer-status-info" style={{
                  background: 'rgba(0, 255, 255, 0.1)',
                  border: '1px solid rgba(0, 255, 255, 0.3)',
                  borderRadius: '12px',
                  padding: '1rem',
                  margin: '1rem 0',
                  textAlign: 'center'
                }}>
                  <div style={{ color: '#00ffff', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                    {state.isProcessingTransfer ? '⏳ 交易确认中...' : '🔄 转会进行中...'}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)' }}>
                    {state.transferTxHash && (
                      <div>交易哈希: {state.transferTxHash.slice(0, 10)}...{state.transferTxHash.slice(-8)}</div>
                    )}
                    请耐心等待区块链确认
                  </div>
                </div>
              )}

              {/* 错误信息显示 */}
              {state.error && (
                <div className="transfer-error-info" style={{
                  background: 'rgba(255, 77, 79, 0.1)',
                  border: '1px solid rgba(255, 77, 79, 0.3)',
                  borderRadius: '12px',
                  padding: '1rem',
                  margin: '1rem 0',
                  textAlign: 'center',
                  color: '#ff4d4f'
                }}>
                  ❌ {state.error}
                </div>
              )}

              <button 
                className="transfer-action-btn"
                onClick={() => actions.transferClub(selectedTransferClub)}
                disabled={state.transferLoading || state.isTransferring || isApproving || (nctBalance !== "--" && parseFloat(nctBalance) < state.transferCost)}
              >
                {(() => {
                  // 如果正在授权中
                  if (isApproving) {
                    return "授权中...";
                  }
                  
                  // 如果正在转会交易中
                  if (state.isTransferring) {
                    return "转会交易中...";
                  }
                  
                  // 如果其他加载状态
                  if (state.transferLoading) {
                    return "处理中...";
                  }
                  
                  // 检查余额是否足够
                  if (nctBalance !== "--" && parseFloat(nctBalance) < state.transferCost) {
                    return "NClab余额不足";
                  }
                  
                  // 检查是否需要授权
                  if (actions.needsApprovalForTransfer && actions.needsApprovalForTransfer(state.transferCost.toString())) {
                    return `授权 ${state.transferCost.toLocaleString()} NClab`;
                  }
                  
                  // 已授权，可以进行转会
                  return `确认转会 (消耗 ${state.transferCost.toLocaleString()} NClab)`;
                })()}
              </button>

              {/* 授权状态重置按钮 - 仅在授权状态异常时显示 */}
              {isApproving && (
                <button 
                  className="transfer-reset-btn"
                  onClick={() => {
                    actions.resetNctState();
                    message.info('授权状态已重置');
                  }}
                  style={{
                    marginTop: '10px',
                    padding: '8px 16px',
                    background: 'rgba(255, 193, 7, 0.1)',
                    border: '1px solid rgba(255, 193, 7, 0.3)',
                    borderRadius: '6px',
                    color: '#ffc107',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  重置授权状态
                </button>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  // 渲染排行榜
  const renderRankings = () => {
    if (state.loading) {
      return (
        <div className="club-rankings">
          <div className="club-loading">
            <Spin size="large" />
            <p style={{ marginTop: '1rem' }}>加载排行榜数据中...</p>
          </div>
        </div>
      );
    }

    if (state.rankings.length === 0) {
      return (
        <div className="club-rankings">
          <div className="club-error">
            <TrophyOutlined style={{ fontSize: '3rem', marginBottom: '1rem' }} />
            <h3>暂无排行榜数据</h3>
          </div>
        </div>
      );
    }

    return (
      <div className="club-rankings">
        <div className="custom-rankings-table">
          <div className="rankings-header">
            <div className="header-cell rank-col">排名</div>
            <div className="header-cell club-col">俱乐部</div>
            <div className="header-cell score-col">总积分</div>
            <div className="header-cell members-col">成员数</div>
            <div className="header-cell avg-col">平均积分</div>
            <div className="header-cell change-col">积分变化</div>
          </div>
          
          <div className="rankings-body">
            {state.rankings.map((record, index) => {
              const rank = record.rank;
              const change = record.scoreChange || 0;
              const changeClass = change > 0 ? 'positive' : change < 0 ? 'negative' : 'neutral';
              const icon = change > 0 ? <RiseOutlined /> : change < 0 ? <FallOutlined /> : <MinusOutlined />;
              
              return (
                <div key={record.rank} className="rankings-row">
                  <div className="table-cell rank-col">
                    <div className={`ranking-position ${rank <= 3 ? `top-${rank}` : ''}`}>
                      {rank <= 3 && <TrophyOutlined />}
                      #{rank}
                    </div>
                  </div>
                  
                  <div className="table-cell club-col">
                    <div className="ranking-club-info">
                      <span className="ranking-club-logo">{record.club.logo}</span>
                      <div className="club-details">
                        <div className="club-name">{record.club.name}</div>
                        <div className="club-kol-name">KOL: {record.club.kol.name}</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="table-cell score-col">
                    <span className="score-value">{record.score.toLocaleString()}</span>
                  </div>
                  
                  <div className="table-cell members-col">
                    <span className="members-value">{record.memberCount}</span>
                  </div>
                  
                  <div className="table-cell avg-col">
                    <span className="avg-value">{record.averageScore.toLocaleString()}</span>
                  </div>
                  
                  <div className="table-cell change-col">
                    <div className={`score-change ${changeClass}`}>
                      {icon}
                      <span>{change > 0 ? '+' : ''}{change}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  if (state.loading && state.clubs.length === 0) {
    return (
      <div className="nova-container">
        <div className="nova-grid"></div>
        <Navbar language={language} />
        <div className="content">
          <div className="club-loading">
            <Spin size="large" />
            <p style={{ marginTop: '1rem' }}>加载俱乐部数据中...</p>
          </div>
        </div>
        <Footer language={language} setLanguage={setLanguage} />
      </div>
    );
  }

  return (
    <div className="nova-container club-page">
      <div className="nova-grid"></div>
      
      <Navbar language={language} />

      <div className="content">
        <div className="club-title-section">
          <h1 className="nova-title">
            <CrownOutlined style={{ marginRight: '0.5rem' }} />
            俱乐部系统
          </h1>
          <p className="nova-subtitle">
            加入KOL战队，共同征战币圈大乱斗
          </p>
        </div>

        <div className="club-main-content">
          <Tabs
            defaultActiveKey="overview"
            className="club-tabs"
            items={[
              {
                key: "overview",
                label: (
                  <span>
                    <UsergroupAddOutlined />
                    俱乐部总览
                  </span>
                ),
                children: renderClubsOverview(),
              },
              {
                key: "my-club",
                label: (
                  <span>
                    <CrownOutlined />
                    我的俱乐部
                  </span>
                ),
                children: renderMyClub(),
              },
              {
                key: "transfer",
                label: (
                  <span>
                    <SwapOutlined />
                    转会中心
                  </span>
                ),
                children: renderTransferCenter(),
              },
              {
                key: "rankings",
                label: (
                  <span>
                    <TrophyOutlined />
                    排行榜
                  </span>
                ),
                children: renderRankings(),
              },
            ]}
          />
        </div>
      </div>

      <Footer language={language} setLanguage={setLanguage} />
    </div>
  );
};

export default ClubPage; 